import { useEffect } from "react";
import { useInitializeEmbeddedCartCurrency } from "~/lib/use-embedded-cart";
import { useCurrencyPreference } from "~/lib/use-currency-preference";
import { useLocalePreference } from "~/lib/use-locale-preference";

const CHECKOUT_DWA_ORIGIN = import.meta.env.VITE_CHECKOUT_DWA_ORIGIN as string | undefined;
const SCRIPT_ID = "embedded-cart-script";
const CART_ELEMENT_ID = "bouncie-embedded-cart";

// checkoutdwa mounts the cart as a bare <div> with no landmark role, so axe flags its
// contents as unlabeled.
function labelCartLandmark(element: HTMLElement) {
  element.setAttribute("role", "region");
  element.setAttribute("aria-label", "Shopping cart");
}

// Loaded via our own BFF proxy (routes/checkout-dwa-proxy.tsx), not checkoutdwa's origin
// directly, to keep the browser on our domain.
//
// Injected post-mount rather than SSR'd: a script present in the SSR'd HTML would run
// mid-hydration, and React would then strip out the div it prepends as an unexpected node.
export function EmbeddedCartScript() {
  useInitializeEmbeddedCartCurrency();
  // Mounting these here (return values unused) guarantees at least one always-mounted
  // subscriber, the same role bouncieweb's ConnectedRoot plays for useCartState — the
  // sync itself runs from any component that calls these, e.g. LocaleDialog too.
  useCurrencyPreference();
  useLocalePreference();

  useEffect(() => {
    if (!CHECKOUT_DWA_ORIGIN || document.getElementById(SCRIPT_ID)) {
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "/embed/bouncie-embedded-cart/cart.min.js";
    script.dataset.config = JSON.stringify({
      checkoutUrl: `${CHECKOUT_DWA_ORIGIN}/order/checkout/shipping`,
    });
    document.body.appendChild(script);

    const existing = document.getElementById(CART_ELEMENT_ID);
    if (existing) {
      labelCartLandmark(existing);
      return;
    }
    const observer = new MutationObserver(() => {
      const cartElement = document.getElementById(CART_ELEMENT_ID);
      if (cartElement) {
        labelCartLandmark(cartElement);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, []);

  return null;
}
